import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, RefreshCw, LogOut, Trash2, Plus, Save, Upload, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/lib/useSeo";
import AdminClients from "@/pages/AdminClients";
import {
  adminLeads, adminUpdateLead, leadsExportUrl, getSite, updateSite,
  getProjects, adminCreateProject, adminUpdateProject, adminDeleteProject,
  getTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial,
  uploadFile, fileUrl,
} from "@/lib/api";

const STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];
const TABS = ["Leads", "Clients", "Site", "Projects", "Testimonials"];
const inp = "w-full bg-transparent border border-olive/20 rounded-sm px-3 py-2 text-sm focus:border-olive focus:outline-none";

export default function Admin() {
  useSeo({ title: "Admin" });
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Leads");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [loading, user, navigate]);

  if (!user || user.role !== "admin") return <div className="pt-40 text-center text-ink/50">Loading…</div>;

  return (
    <div className="pt-32 pb-24 mx-auto max-w-[1400px] px-6 md:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <span className="overline text-bronze">Admin</span>
          <h1 className="font-display text-4xl text-olive">Content & Leads</h1>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout"
          className="inline-flex items-center gap-2 border border-olive/30 text-olive px-5 py-3 rounded-sm text-sm uppercase tracking-[0.2em] font-bold hover:bg-olive hover:text-beige transition-colors">
          <LogOut size={15} /> Log out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-10 border-b border-olive/15">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-5 py-3 text-sm uppercase tracking-[0.2em] font-bold -mb-px border-b-2 transition-colors ${tab === t ? "border-olive text-olive" : "border-transparent text-ink/50 hover:text-olive"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Leads" && <LeadsTab />}
      {tab === "Clients" && <AdminClients />}
      {tab === "Site" && <SiteTab />}
      {tab === "Projects" && <ProjectsTab />}
      {tab === "Testimonials" && <TestimonialsTab />}
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const load = useCallback(() => {
    adminLeads().then(setLeads).catch(() => toast.error("Failed to load leads"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await adminUpdateLead(id, { status });
      setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="leads-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-ink/50 text-sm">{leads.length} submissions</p>
        <div className="flex gap-3">
          <button onClick={load} className="border border-olive/25 px-4 py-2.5 rounded-sm text-sm uppercase tracking-[0.2em] font-bold flex items-center gap-2 hover:bg-cream"><RefreshCw size={14} /> Refresh</button>
          <a href={leadsExportUrl()} data-testid="admin-export" className="bg-olive text-beige px-4 py-2.5 rounded-sm text-sm uppercase tracking-[0.2em] font-bold flex items-center gap-2 hover:bg-[#21463c]"><Download size={14} /> Export CSV</a>
        </div>
      </div>
      <div className="overflow-x-auto border border-olive/15 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-ink/60 uppercase tracking-wider text-[0.65rem]">
            <tr>{["Name", "Phone", "Service", "Budget", "Location", "Date", "Status"].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-olive/10 hover:bg-cream/40" data-testid="admin-lead-row">
                <td className="px-4 py-3 whitespace-nowrap font-medium">{l.name}</td>
                <td className="px-4 py-3 whitespace-nowrap">{l.phone}</td>
                <td className="px-4 py-3 whitespace-nowrap">{l.service_required || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">{l.budget || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">{l.location || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap text-ink/50">{(l.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="bg-transparent border border-olive/30 rounded px-2 py-1 text-xs focus:outline-none">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length === 0 && <p className="text-center text-ink/50 py-12">No leads yet.</p>}
      </div>
    </div>
  );
}

function SiteTab() {
  const [f, setF] = useState(null);
  useEffect(() => { getSite().then(setF); }, []);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    try { await updateSite(f); toast.success("Site content updated — refresh the homepage to see it."); }
    catch { toast.error("Save failed"); }
  };
  if (!f) return <p className="text-ink/50">Loading…</p>;
  return (
    <div data-testid="site-tab" className="max-w-2xl space-y-6">
      <Labeled label="Hero Title (comma separates lines)"><input data-testid="site-hero-title" className={inp} value={f.hero_title || ""} onChange={up("hero_title")} /></Labeled>
      <Labeled label="Hero Subtitle"><textarea rows={2} className={inp} value={f.hero_subtitle || ""} onChange={up("hero_subtitle")} /></Labeled>
      <Labeled label="Hero Image URL"><input data-testid="site-hero-image" className={inp} value={f.hero_image || ""} onChange={up("hero_image")} /></Labeled>
      {f.hero_image && <img src={f.hero_image} alt="preview" className="w-full h-48 object-cover rounded-md border border-olive/15" />}
      <Labeled label="About Text"><textarea rows={3} className={inp} value={f.about_text || ""} onChange={up("about_text")} /></Labeled>
      <button onClick={save} data-testid="site-save" className="inline-flex items-center gap-2 bg-olive text-beige px-8 py-3 rounded-sm text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#21463c]"><Save size={15} /> Save Changes</button>
    </div>
  );
}

function ProjectImageUpload({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = value && value.startsWith("/api/files/") ? fileUrl(value) : value;
  const onPick = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(true);
    try { const r = await uploadFile(file, "photo"); onChange(r.url); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="flex gap-2 items-stretch">
      <input placeholder="Image URL or upload →" className={`${inp} flex-1`} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onPick} data-testid={testid && `${testid}-file`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 border border-olive text-olive px-3 rounded-sm text-xs uppercase tracking-[0.15em] hover:bg-olive hover:text-beige transition-colors shrink-0">
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
      </button>
      {preview && <img src={preview} alt="" className="w-14 h-10 object-cover rounded-sm border border-olive/15" />}
    </div>
  );
}

function ProjectsTab() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => getProjects().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  const blank = { title: "", category: "Residential", location: "", image: "", year: "2024" };
  const [draft, setDraft] = useState(blank);

  const create = async () => {
    if (!draft.title || !draft.image) return toast.error("Title and image required");
    try { await adminCreateProject(draft); setDraft(blank); load(); toast.success("Project added"); }
    catch { toast.error("Failed"); }
  };
  const save = async (p) => { try { await adminUpdateProject(p.slug, p); toast.success("Saved"); } catch { toast.error("Failed"); } };
  const del = async (slug) => { try { await adminDeleteProject(slug); load(); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const edit = (i, k, v) => setItems((arr) => arr.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  return (
    <div data-testid="projects-tab" className="space-y-8">
      <div className="border border-olive/20 rounded-md p-6 bg-cream/50">
        <p className="overline text-bronze mb-4">Add Project</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input placeholder="Title" data-testid="proj-new-title" className={inp} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <input placeholder="Category" className={inp} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
          <input placeholder="Location" className={inp} value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
        </div>
        <div className="mt-3">
          <ProjectImageUpload value={draft.image} onChange={(v) => setDraft({ ...draft, image: v })} testid="proj-new-image" />
        </div>
        <button onClick={create} data-testid="proj-add" className="mt-4 inline-flex items-center gap-2 bg-olive text-beige px-6 py-2.5 rounded-sm text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#21463c]"><Plus size={15} /> Add</button>
      </div>
      <div className="space-y-4">
        {items.map((p, i) => (
          <div key={p.slug} className="grid lg:grid-cols-[120px_1fr_auto] gap-4 items-center border border-olive/15 rounded-md p-4">
            <img src={p.image && p.image.startsWith("/api/files/") ? fileUrl(p.image) : p.image} alt={p.title} className="w-full h-20 object-cover rounded" />
            <div className="grid gap-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <input className={inp} value={p.title} onChange={(e) => edit(i, "title", e.target.value)} />
                <input className={inp} value={p.category} onChange={(e) => edit(i, "category", e.target.value)} />
                <input className={inp} value={p.location || ""} onChange={(e) => edit(i, "location", e.target.value)} />
              </div>
              <ProjectImageUpload value={p.image} onChange={(v) => edit(i, "image", v)} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => save(p)} className="p-2.5 bg-olive text-beige rounded-sm hover:bg-[#21463c]"><Save size={15} /></button>
              <button onClick={() => del(p.slug)} className="p-2.5 border border-red-300 text-red-600 rounded-sm hover:bg-red-50"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => getTestimonials().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  const blank = { name: "", location: "", project: "", rating: 5, quote: "" };
  const [draft, setDraft] = useState(blank);

  const create = async () => {
    if (!draft.name || !draft.quote) return toast.error("Name and quote required");
    try { await adminCreateTestimonial({ ...draft, rating: Number(draft.rating) }); setDraft(blank); load(); toast.success("Added"); }
    catch { toast.error("Failed"); }
  };
  const save = async (t) => { try { await adminUpdateTestimonial(t.name, { ...t, rating: Number(t.rating) }); toast.success("Saved"); } catch { toast.error("Failed"); } };
  const del = async (name) => { try { await adminDeleteTestimonial(name); load(); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const edit = (i, k, v) => setItems((arr) => arr.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  return (
    <div data-testid="testimonials-tab" className="space-y-8">
      <div className="border border-olive/20 rounded-md p-6 bg-cream/50">
        <p className="overline text-bronze mb-4">Add Testimonial</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input placeholder="Name" data-testid="test-new-name" className={inp} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input placeholder="Location" className={inp} value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
          <input placeholder="Project" className={inp} value={draft.project} onChange={(e) => setDraft({ ...draft, project: e.target.value })} />
          <input placeholder="Rating 1-5" type="number" min="1" max="5" className={inp} value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: e.target.value })} />
        </div>
        <textarea placeholder="Quote" data-testid="test-new-quote" className={`${inp} mt-3`} rows={2} value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} />
        <button onClick={create} data-testid="test-add" className="mt-4 inline-flex items-center gap-2 bg-olive text-beige px-6 py-2.5 rounded-sm text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#21463c]"><Plus size={15} /> Add</button>
      </div>
      <div className="space-y-4">
        {items.map((t, i) => (
          <div key={t.name + i} className="border border-olive/15 rounded-md p-4 space-y-3">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input className={inp} value={t.name} onChange={(e) => edit(i, "name", e.target.value)} />
              <input className={inp} value={t.location || ""} onChange={(e) => edit(i, "location", e.target.value)} />
              <input className={inp} value={t.project || ""} onChange={(e) => edit(i, "project", e.target.value)} />
              <input type="number" min="1" max="5" className={inp} value={t.rating} onChange={(e) => edit(i, "rating", e.target.value)} />
            </div>
            <textarea className={inp} rows={2} value={t.quote} onChange={(e) => edit(i, "quote", e.target.value)} />
            <div className="flex gap-2">
              <button onClick={() => save(t)} className="p-2.5 bg-olive text-beige rounded-sm hover:bg-[#21463c]"><Save size={15} /></button>
              <button onClick={() => del(t.name)} className="p-2.5 border border-red-300 text-red-600 rounded-sm hover:bg-red-50"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="overline text-bronze block mb-2">{label}</label>
      {children}
    </div>
  );
}
